import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';

// ── Utilidades ────────────────────────────────────────────────────────────────

function Spinner() {
  return <div className="w-6 h-6 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto" />;
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';
const labelCls = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1';

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

const CATEGORIAS = [
  'Aerotermia', 'Climatización', 'Fontanería', 'Material eléctrico',
  'Herramientas', 'Recambios', 'Transporte', 'Consumibles', 'Otro',
];

const EMPTY_FORM = {
  nombre: '', nif: '', contacto: '', telefono: '', email: '',
  web: '', categoria: '', notas: '', activo: true,
};

// ── Formulario de proveedor ───────────────────────────────────────────────────

function ProveedorForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Nombre *">
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre del proveedor" />
        </Field>
        <Field label="NIF / CIF">
          <input className={inputCls} value={form.nif} onChange={e => set('nif', e.target.value)} placeholder="B12345678" />
        </Field>
        <Field label="Persona de contacto">
          <input className={inputCls} value={form.contacto} onChange={e => set('contacto', e.target.value)} placeholder="Nombre y apellido" />
        </Field>
        <Field label="Teléfono">
          <input className={inputCls} value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="600 000 000" />
        </Field>
        <Field label="Email">
          <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="pedidos@proveedor.com" />
        </Field>
        <Field label="Web">
          <input className={inputCls} value={form.web} onChange={e => set('web', e.target.value)} placeholder="https://proveedor.com" />
        </Field>
        <Field label="Categoría">
          <select className={inputCls} value={form.categoria} onChange={e => set('categoria', e.target.value)}>
            <option value="">— Seleccionar —</option>
            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Estado">
          <select className={inputCls} value={form.activo ? 'activo' : 'inactivo'} onChange={e => set('activo', e.target.value === 'activo')}>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </Field>
      </div>
      <Field label="Notas">
        <textarea className={inputCls} rows={3} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Condiciones de pago, plazos, observaciones..." />
      </Field>
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.nombre.trim()}
          className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button onClick={onCancel} className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Panel de alertas de stock bajo ───────────────────────────────────────────

function StockAlertsPanel({ proveedores }) {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Firestore no puede comparar dos campos entre sí (stock <= mínimo) en una
  // consulta, así que la reducción real viene de filtrar por
  // `minimum-stock` > 0 del lado del servidor — el resto del catálogo
  // (productos sin mínimo configurado) ni siquiera se descarga. Misma
  // consulta que ya usa alertaStockMinimo.js en el backend. Carga puntual,
  // no listener en vivo — ver el mismo criterio en AlmacenView.js.
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'productos'), where('minimum-stock', '>', 0), where('inactive', '==', false), limit(2000));
    getDocs(q).then(snap => {
      setProductos(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id, ...data,
          nombre: data.name || '', referencia: data.reference || data['full-reference'] || '',
          stock: data['real-stock'] ?? 0, stockMinimo: data['minimum-stock'] ?? 0,
        };
      }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const bajoStock = useMemo(() =>
    productos.filter(p => {
      const stock = Number(p.stock ?? 0);
      const minimo = Number(p.stockMinimo ?? p.stockMin ?? 0);
      return minimo > 0 && stock <= minimo;
    }),
    [productos]
  );

  const sinProveedor = bajoStock.filter(p => !p.proveedorId && !p.proveedor);
  const conProveedor = bajoStock.filter(p => p.proveedorId || p.proveedor);

  const getProveedor = (p) => {
    if (p.proveedorId) return proveedores.find(pv => pv.id === p.proveedorId);
    if (p.proveedor) return proveedores.find(pv => pv.nombre === p.proveedor);
    return null;
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  if (bajoStock.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-5xl mb-3">✅</div>
        <p className="text-gray-500 font-medium">Todos los productos tienen stock suficiente</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-red-600">{bajoStock.length}</div>
          <div className="text-xs text-red-500 font-semibold uppercase mt-1">Alertas totales</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-orange-600">{conProveedor.length}</div>
          <div className="text-xs text-orange-500 font-semibold uppercase mt-1">Con proveedor asignado</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-yellow-600">{sinProveedor.length}</div>
          <div className="text-xs text-yellow-500 font-semibold uppercase mt-1">Sin proveedor</div>
        </div>
      </div>

      {/* Lista de alertas */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Productos con stock bajo</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {bajoStock.map(p => {
            const pv = getProveedor(p);
            const stock = Number(p.stock ?? 0);
            const minimo = Number(p.stockMinimo ?? p.stockMin ?? 0);
            const pct = minimo > 0 ? Math.round((stock / minimo) * 100) : 0;
            return (
              <div key={p.id} className="px-4 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 text-sm truncate">{p.nombre || p.referencia || p.id}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Ref: {p.referencia || '—'} · Categoría: {p.categoria || '—'}
                  </div>
                  {/* Barra de stock */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${pct === 0 ? 'bg-red-600' : pct < 50 ? 'bg-orange-500' : 'bg-yellow-500'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <span className={`text-xs font-bold ${pct === 0 ? 'text-red-600' : pct < 50 ? 'text-orange-600' : 'text-yellow-600'}`}>
                      {stock} / {minimo}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {pv ? (
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-gray-700">{pv.nombre}</div>
                      {pv.telefono && (
                        <a href={`tel:${pv.telefono}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 justify-end">
                          📞 {pv.telefono}
                        </a>
                      )}
                      {pv.email && (
                        <a
                          href={`mailto:${pv.email}?subject=Pedido de material — ${p.nombre || p.referencia}&body=Hola,%0A%0ANecesitamos reponer el siguiente material:%0A%0A- ${p.nombre || p.referencia} (Ref: ${p.referencia || '—'})%0A  Stock actual: ${stock} uds%0A  Stock mínimo: ${minimo} uds%0A%0AGracias.`}
                          className="text-xs text-red-600 hover:underline flex items-center gap-1 justify-end"
                        >
                          ✉️ Pedir por email
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Sin proveedor</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function ProveedoresView() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('lista'); // 'lista' | 'alertas'
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/.netlify/functions/getProveedores', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        setProveedores(d.success ? d.proveedores : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = proveedores;
    if (catFilter) list = list.filter(p => p.categoria === catFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(p =>
        (p.nombre || '').toLowerCase().includes(s) ||
        (p.contacto || '').toLowerCase().includes(s) ||
        (p.email || '').toLowerCase().includes(s) ||
        (p.nif || '').toLowerCase().includes(s)
      );
    }
    return list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [proveedores, search, catFilter]);

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      const payload = editing ? { ...form, id: editing.id } : form;
      const resp = await fetch('/.netlify/functions/saveProveedor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error al guardar');

      if (editing) {
        setProveedores(prev => prev.map(p => p.id === editing.id ? { ...p, ...form } : p));
      } else {
        setProveedores(prev => [...prev, { id: data.id, ...form }]);
      }
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => { setEditing(null); setShowForm(true); setError(''); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); setError(''); };
  const cancelForm = () => { setShowForm(false); setEditing(null); setError(''); };

  const categorias = [...new Set(proveedores.map(p => p.categoria).filter(Boolean))].sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Proveedores y Stock</h2>
            <p className="text-gray-500 text-sm mt-1">Gestión de proveedores y alertas de stock mínimo</p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
          >
            <span className="text-lg leading-none">+</span> Nuevo proveedor
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-gray-100">
          {[
            { id: 'lista', label: `Lista (${proveedores.filter(p => p.activo !== false).length})` },
            { id: 'alertas', label: 'Alertas de stock' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Formulario modal */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          <h3 className="font-bold text-gray-800 mb-4">{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</h3>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-2 mb-4">{error}</div>}
          <ProveedorForm
            initial={editing || EMPTY_FORM}
            onSave={handleSave}
            onCancel={cancelForm}
            saving={saving}
          />
        </div>
      )}

      {/* Contenido por tab */}
      {tab === 'alertas' ? (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          <StockAlertsPanel proveedores={proveedores} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-4">
          {/* Filtros */}
          <div className="flex gap-3">
            <input
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
              placeholder="Buscar por nombre, NIF, contacto…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
              value={catFilter}
              onChange={e => setCatFilter(e.target.value)}
            >
              <option value="">Todas las categorías</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Tabla */}
          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              {proveedores.length === 0 ? 'No hay proveedores. Añade el primero.' : 'Sin resultados.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Proveedor</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Contacto</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Teléfono</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Email</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Categoría</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase">Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 group">
                      <td className="py-3 px-3">
                        <div className="font-semibold text-gray-800">{p.nombre}</div>
                        {p.nif && <div className="text-xs text-gray-400">{p.nif}</div>}
                      </td>
                      <td className="py-3 px-3 text-gray-600">{p.contacto || '—'}</td>
                      <td className="py-3 px-3">
                        {p.telefono
                          ? <a href={`tel:${p.telefono}`} className="text-blue-600 hover:underline">{p.telefono}</a>
                          : '—'}
                      </td>
                      <td className="py-3 px-3">
                        {p.email
                          ? <a href={`mailto:${p.email}`} className="text-red-600 hover:underline truncate block max-w-[180px]">{p.email}</a>
                          : '—'}
                      </td>
                      <td className="py-3 px-3">
                        {p.categoria && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{p.categoria}</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          p.activo === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                        }`}>
                          {p.activo === false ? 'Inactivo' : 'Activo'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <button
                          onClick={() => openEdit(p)}
                          className="text-xs text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity font-semibold"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
